import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Edit, Plus, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { partnerAdminKeys } from "@/constant/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { apiRequestV2, parseNormalizedError, queryClient } from "@/lib/queryClient";
import { getFeaturingPartnersOptions, uploadAdminImage } from "@/services/queryOptions/partnerManagementQueryOptions";
import type { OuraListParams } from "@/types/oura";
import type { FeaturingPartnerAdmin } from "@/types/partner-management";
import { AdminDataTable } from "../components/AdminDataTable";

type FormValues = {
  name: string;
  logoUrl: string;
  webUrl: string;
  xUrl: string;
  discordUrl: string;
  telegramUrl: string;
  instagramUrl: string;
};

const emptyForm: FormValues = {
  name: "",
  logoUrl: "",
  webUrl: "",
  xUrl: "",
  discordUrl: "",
  telegramUrl: "",
  instagramUrl: "",
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function sameValue(current: unknown, previous: unknown) {
  return String(current ?? "") === String(previous ?? "");
}

function blockerDescription(details: unknown) {
  if (!details || typeof details !== "object") return "";
  return Object.entries(details as Record<string, number>)
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${key}: ${count}`)
    .join(", ");
}

function LogoField({
  value,
  file,
  onChange,
  onFileChange,
}: {
  value: string;
  file: File | null;
  onChange: (value: string) => void;
  onFileChange: (file: File | null) => void;
}) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(nextPreviewUrl);
    onChange(`https://local-upload.tweetonium/${encodeURIComponent(file.name)}`);

    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [file]);

  const preview = previewUrl || value;

  return (
    <div className="space-y-2 sm:col-span-2">
      <Label>Logo</Label>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(event) => {
            onFileChange(null);
            onChange(event.target.value);
          }}
          placeholder="https://assets.example.com/path/logo.png"
        />
        <label className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md border border-input px-3 text-sm">
          <Upload className="mr-2 h-4 w-4" />
          Choose
          <input
            className="hidden"
            type="file"
            accept=".png,.avif,.jpg,.jpeg,.webp"
            onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
          />
        </label>
      </div>
      {file && <div className="text-xs text-zinc-400">Selected: {file.name}</div>}
      {preview && <img src={preview} alt="" className="h-16 w-16 rounded border border-zinc-800 object-cover" />}
    </div>
  );
}

function FeaturedPartnerDialog({
  open,
  item,
  onOpenChange,
}: {
  open: boolean;
  item?: FeaturingPartnerAdmin | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState<FormValues>(emptyForm);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  useEffect(() => {
    setForm(item ? {
      name: item.name,
      logoUrl: item.logoUrl ?? "",
      webUrl: item.webUrl ?? "",
      xUrl: item.xUrl ?? "",
      discordUrl: item.discordUrl ?? "",
      telegramUrl: item.telegramUrl ?? "",
      instagramUrl: item.instagramUrl ?? "",
    } : emptyForm);
    setLogoFile(null);
  }, [item, open]);

  const mutation = useMutation({
    mutationFn: async () => {
      const uploadedLogo = logoFile
        ? (await uploadAdminImage(logoFile, "partner", slugify(form.name), "featuring-partner-logo")).objectName
        : undefined;
      const payload: Record<string, unknown> = {};
      const addField = (key: keyof FormValues, value: unknown, changed: boolean) => {
        if (!item || changed) payload[key] = value;
      };

      addField("name", form.name.trim(), item ? !sameValue(form.name.trim(), item.name) : true);
      addField("webUrl", form.webUrl.trim() || null, item ? !sameValue(form.webUrl.trim(), item.webUrl) : true);
      addField("xUrl", form.xUrl.trim() || null, item ? !sameValue(form.xUrl.trim(), item.xUrl) : true);
      addField("discordUrl", form.discordUrl.trim() || null, item ? !sameValue(form.discordUrl.trim(), item.discordUrl) : true);
      addField("telegramUrl", form.telegramUrl.trim() || null, item ? !sameValue(form.telegramUrl.trim(), item.telegramUrl) : true);
      addField("instagramUrl", form.instagramUrl.trim() || null, item ? !sameValue(form.instagramUrl.trim(), item.instagramUrl) : true);

      if (uploadedLogo !== undefined || (!item && form.logoUrl.trim())) {
        payload.logoUrl = uploadedLogo ?? form.logoUrl.trim();
      } else if (item && !sameValue(form.logoUrl.trim(), item.logoUrl)) {
        payload.logoUrl = form.logoUrl.trim() || null;
      }

      if (item && Object.keys(payload).length === 0) return item;

      return apiRequestV2(
        item ? "PATCH" : "POST",
        item ? `/api/v1/featuring-partner/${item.id}` : "/api/v1/featuring-partner",
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.featuringPartners });
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: item ? "Featured partner updated" : "Featured partner created" });
      onOpenChange(false);
    },
    onError: (error) => {
      const normalized = parseNormalizedError(error);
      const details = blockerDescription(normalized.details);
      toast({
        title: item ? "Cannot update featured partner" : "Cannot create featured partner",
        description: details ? `${normalized.message} (${details})` : normalized.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{item ? "Edit Featured Partner" : "Add Featured Partner"}</DialogTitle>
          <DialogDescription>Manage the simple featured partner profile shown on partner pages.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label>Name</Label>
            <Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Partner name" />
          </div>
          <LogoField
            value={form.logoUrl}
            file={logoFile}
            onChange={(logoUrl) => setForm((current) => ({ ...current, logoUrl }))}
            onFileChange={setLogoFile}
          />
          <div className="space-y-2">
            <Label>Website</Label>
            <Input value={form.webUrl} onChange={(event) => setForm((current) => ({ ...current, webUrl: event.target.value }))} placeholder="https://example.com" />
          </div>
          <div className="space-y-2">
            <Label>X URL</Label>
            <Input value={form.xUrl} onChange={(event) => setForm((current) => ({ ...current, xUrl: event.target.value }))} placeholder="https://x.com/example" />
          </div>
          <div className="space-y-2">
            <Label>Discord URL</Label>
            <Input value={form.discordUrl} onChange={(event) => setForm((current) => ({ ...current, discordUrl: event.target.value }))} placeholder="https://discord.gg/example" />
          </div>
          <div className="space-y-2">
            <Label>Telegram URL</Label>
            <Input value={form.telegramUrl} onChange={(event) => setForm((current) => ({ ...current, telegramUrl: event.target.value }))} placeholder="https://t.me/example" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Instagram URL</Label>
            <Input value={form.instagramUrl} onChange={(event) => setForm((current) => ({ ...current, instagramUrl: event.target.value }))} placeholder="https://instagram.com/example" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.name.trim()}>
            {mutation.isPending ? "Saving..." : item ? "Update Featured Partner" : "Create Featured Partner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FeaturedPartnerManager() {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25 });
  const [dialogItem, setDialogItem] = useState<FeaturingPartnerAdmin | null | undefined>();
  const query = useQuery(getFeaturingPartnersOptions());
  const search = params.search?.trim().toLowerCase();
  const rows = (query.data ?? []).filter((item) => !search || [
    String(item.id),
    item.name,
    item.webUrl ?? "",
    item.xUrl ?? "",
    item.discordUrl ?? "",
    item.telegramUrl ?? "",
    item.instagramUrl ?? "",
  ].some((value) => value.toLowerCase().includes(search)));
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/featuring-partner/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.featuringPartners });
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: "Featured partner deleted" });
    },
    onError: (error) => {
      const normalized = parseNormalizedError(error);
      const details = blockerDescription(normalized.details);
      toast({
        title: "Cannot delete featured partner",
        description: details ? `${normalized.message} (${details})` : normalized.message,
        variant: "destructive",
      });
    },
  });
  const columns = useMemo<ColumnDef<FeaturingPartnerAdmin>[]>(() => [
    {
      accessorKey: "logoUrl",
      header: "Logo",
      cell: ({ row }) => row.original.logoUrl ? <img src={row.original.logoUrl} alt="" className="h-10 w-10 rounded object-cover" /> : "-",
    },
    { accessorKey: "name", header: "Name" },
    { accessorKey: "webUrl", header: "Website", cell: ({ row }) => row.original.webUrl || "-" },
    { accessorKey: "xUrl", header: "X", cell: ({ row }) => row.original.xUrl || "-" },
    { accessorKey: "discordUrl", header: "Discord", cell: ({ row }) => row.original.discordUrl || "-" },
    {
      accessorKey: "updatedAt",
      header: "Updated",
      cell: ({ row }) => row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleString() : "-",
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setDialogItem(row.original)} aria-label="Edit featured partner">
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (!window.confirm("Delete this featured partner? This action cannot be undone.")) return;
              del.mutate(row.original.id);
            }}
            aria-label="Delete featured partner"
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ),
    },
  ], [del]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Featured Partners</h2>
        <p className="text-sm text-zinc-400">Manage simple featured partner profiles used by partner pages.</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Featured Partner List</CardTitle>
          <Button onClick={() => setDialogItem(null)}><Plus className="mr-2 h-4 w-4" />Add Featured Partner</Button>
        </CardHeader>
        <CardContent>
          <AdminDataTable
            columns={columns}
            data={rows}
            total={rows.length}
            params={params}
            onParamsChange={setParams}
            exportName="featured-partners.csv"
            searchPlaceholder="Search featured partners..."
          />
        </CardContent>
      </Card>
      <FeaturedPartnerDialog
        open={dialogItem !== undefined}
        item={dialogItem || null}
        onOpenChange={(open) => !open && setDialogItem(undefined)}
      />
    </div>
  );
}
